import * as React from "react";
import { cva } from "class-variance-authority";

export const textVariants = cva("transition-colors", {
  variants: {
    variant: {
      hero: "font-heading text-hero font-black leading-[0.9] tracking-tighter",
      display: "font-heading text-display font-black leading-tight tracking-tight",
      "display-xl":
        "font-heading text-[clamp(3rem,6vw,6rem)] font-bold leading-[1.05] tracking-tight",
      h1: "font-heading text-5xl font-bold tracking-tight",
      h2: "font-heading text-4xl font-bold tracking-tight",
      h3: "font-heading text-2xl font-semibold tracking-tight",
      body: "font-sans text-base leading-relaxed",
      small: "font-sans text-sm leading-normal",
      mono: "font-mono text-xs font-semibold uppercase tracking-widest",
    },
    intent: {
      default: "text-primary",
      muted: "text-secondary",
      primary: "text-primary",
      accent: "text-accent",
      gradient: "text-gradient w-fit",
    },
  },
});

const Highlight = ({ children }: { children: React.ReactNode }) => (
  <span className="font-semibold text-accent">{children}</span>
);

const parseHighlight = (content: React.ReactNode) => {
  if (typeof content !== "string") return content;

  return content.split(/(<highlight>.*?<\/highlight>)/g).map((part, index) => {
    if (part.startsWith("<highlight>") && part.endsWith("</highlight>")) {
      return <Highlight key={`${part}-${index}`}>{part.slice(11, -12)}</Highlight>;
    }
    return part;
  });
};

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  variant: "hero" | "display" | "display-xl" | "h1" | "h2" | "h3" | "body" | "small" | "mono";
  intent: "default" | "muted" | "primary" | "accent" | "gradient";
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span" | "div";
}

export const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ as: Component = "p", children, className, intent, variant, ...props }, ref) => {
    // `Tag` is chosen at runtime, so React cannot narrow the ref to one element
    // type. The cast keeps the public API (a single HTMLElement ref) honest.
    const Tag = Component as React.ElementType;
    const forwardedRef = ref as React.Ref<HTMLElement> | undefined;

    return (
      <Tag
        ref={forwardedRef}
        className={textVariants({ intent, variant, className })}
        {...props}
      >
        {parseHighlight(children)}
      </Tag>
    );
  }
);

Text.displayName = "Text";
